import { issueMessage } from '../api/errors';
import type { ApiIssue } from '../api/types';
import type { Dictionary } from '../i18n/en';
import type { SummaryItem } from '../ui/FormErrors';

export interface FieldDescriptor {
  label: string;
  /** data-field name of the input, when the field is on the page. */
  fieldName?: string;
}

/**
 * Error summary entries for every issue the server returned: the field's label (or none for
 * form-level problems) and the localized message. Nothing is dropped, even unknown paths.
 */
export function summaryItems(
  t: Dictionary,
  issues: ApiIssue[],
  describe: (path: string) => FieldDescriptor | null,
): SummaryItem[] {
  return issues.map((issue, index) => {
    const field = describe(issue.path);
    return {
      id: `${issue.path}:${issue.issue}:${index}`,
      label: field?.label ?? null,
      message: issueMessage(t, issue),
      fieldName: field?.fieldName,
    };
  });
}

/** Describes paths from a flat map { path: label } (data-field name = path). */
export function describeFrom(
  labels: Record<string, string>,
): (path: string) => FieldDescriptor | null {
  return (path) => {
    const label = labels[path];
    return label ? { label, fieldName: path } : null;
  };
}

/** Trimmed text or undefined, so optional fields are omitted from request bodies. */
export function optionalText(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}
